import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { getProfile, type Profile } from '../lib/profile';

interface AuthProfileContextValue {
  session: Session | null;
  user: User | null;
  isAuthenticated: boolean;
  profile: Profile | null;
  profileLoading: boolean;
  refreshProfile: () => Promise<void>;
}

const AuthProfileContext = createContext<AuthProfileContextValue | undefined>(undefined);

interface AuthProfileProviderProps {
  session: Session | null;
  children: React.ReactNode;
}

export function AuthProfileProvider({ session, children }: AuthProfileProviderProps) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const user = session?.user ?? null;
  const isAuthenticated = !!user;

  const refreshProfile = async () => {
    if (!user) {
      setProfile(null);
      return;
    }

    setProfileLoading(true);
    try {
      const data = await getProfile(user.id);
      setProfile(data);
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }
    refreshProfile();
  }, [user?.id]);

  const value = useMemo<AuthProfileContextValue>(() => ({
    session,
    user,
    isAuthenticated,
    profile,
    profileLoading,
    refreshProfile,
  }), [session, user, isAuthenticated, profile, profileLoading]);

  return (
    <AuthProfileContext.Provider value={value}>
      {children}
    </AuthProfileContext.Provider>
  );
}

export function useAuthProfile() {
  const context = useContext(AuthProfileContext);
  if (!context) {
    throw new Error('useAuthProfile must be used within AuthProfileProvider');
  }
  return context;
}

